/*
 * useEventListener  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { useEffect, useRef } from "react";
import { isFunction } from "@adapptio/lib-shared";
import { useDeepCompareMemo } from "use-deep-compare";

export function useEventListener<
	EventKeyDocument extends keyof DocumentEventMap,
	EventKeyHTML extends keyof HTMLElementEventMap,
	EventKeyWindow extends keyof WindowEventMap,
	Listener extends (
		event:
			| DocumentEventMap[EventKeyDocument]
			| HTMLElementEventMap[EventKeyHTML]
			| WindowEventMap[EventKeyWindow]
	) => void
>(
	element: Document | HTMLElement | Window | null | undefined,
	eventName: EventKeyDocument | EventKeyHTML | EventKeyWindow | string,
	listener: Listener,
	options?: boolean | AddEventListenerOptions,
	active = true
): void {
	const savedListener = useRef<Listener>();
	const memoizedOptions = useDeepCompareMemo(() => options, [ options ]);

	useEffect(() => {
		savedListener.current = listener;
	}, [ listener ]);

	useEffect(() => {
		if (!isFunction(element?.addEventListener) || !active) {
			return;
		}

		const eventListener = (event) => savedListener.current(event);

		element.addEventListener(eventName, eventListener, memoizedOptions);

		return () => {
			element.removeEventListener(eventName, eventListener, memoizedOptions);
		};
	}, [ eventName, element, memoizedOptions, active ]);
}
